import { useState, useEffect, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthContext } from "@/App";
import FeedbackSection from "@/components/FeedbackSection";
import { ArrowLeft, User, CreditCard, Calendar, CheckCircle, LogOut, Crown, Loader2, Shield, Clock, Pencil, Trash2, X, Save, Users } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useContext(AuthContext);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchSchedules();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { withCredentials: true });
      
      if (response.data.payment_status === 'paid') {
        toast.success(response.data.message || "Subscription activated!");
        fetchSubscriptionStatus();
        // Remove session_id from URL
        window.history.replaceState({}, '', '/account');
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        window.history.replaceState({}, '', '/account');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      toast.error("Error verifying payment. Please refresh the page.");
    }
  };

  const handleSubscribe = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan: plan,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedules`, { withCredentials: true });
      setSchedules(response.data);
    } catch (error) {
      console.error("Error fetching schedules:", error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const startEditing = (schedule) => {
    setEditingId(schedule.id);
    setEditForm({
      role: schedule.role,
      prompt: schedule.prompt,
      focus_areas: schedule.focus_areas,
      frequency: schedule.frequency,
      day_of_week: schedule.day_of_week || "Monday",
      time: schedule.time,
      colleague_emails: (schedule.colleague_emails || []).join(", "),
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveSchedule = async (scheduleId) => {
    setSavingId(scheduleId);
    try {
      await axios.put(`${API}/schedules/${scheduleId}`, {
        role: editForm.role,
        prompt: editForm.prompt,
        focus_areas: editForm.focus_areas,
        frequency: editForm.frequency,
        day_of_week: editForm.frequency === "weekly" ? editForm.day_of_week : null,
        time: editForm.time,
        colleague_emails: editForm.colleague_emails || null,
      }, { withCredentials: true });
      toast.success("Schedule updated");
      setEditingId(null);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update schedule");
    } finally {
      setSavingId(null);
    }
  };

  const deleteSchedule = async (scheduleId) => {
    setDeletingId(scheduleId);
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`, { withCredentials: true });
      toast.success("Schedule cancelled");
      setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel schedule");
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;
    
    const statusColors = {
      admin: "bg-purple-600 hover:bg-purple-600",
      active: "bg-emerald-600 hover:bg-emerald-600",
      trial: "bg-blue-600 hover:bg-blue-600",
      expired: "bg-red-600 hover:bg-red-600"
    };
    
    const statusLabels = {
      admin: "Admin",
      active: "Active",
      trial: "Free Trial",
      expired: "Expired"
    };
    
    return (
      <Badge className={statusColors[subscriptionStatus.status] || "bg-gray-600"}>
        {statusLabels[subscriptionStatus.status] || subscriptionStatus.status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif" 
                alt="AI Newsletter Logo"
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">AI Newsletter</h1>
                <p className="text-xs text-slate-500">Powered by Owl Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(subscriptionStatus?.is_admin) && (
                <Button
                  data-testid="admin-subscribers-link"
                  onClick={() => navigate('/admin/subscribers')}
                  variant="ghost"
                  className="text-purple-700 hover:text-purple-900 hover:bg-purple-50 font-medium"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Subscribers
                </Button>
              )}
              <Button
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile Card */}
            <Card className="shadow-sm border border-slate-200" data-testid="profile-card">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">Profile</CardTitle>
                  {user?.picture && (
                    <img src={user.picture} alt="Profile" className="h-12 w-12 rounded-full border-2 border-white shadow-sm" />
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Name</p>
                    <p className="font-medium text-slate-900" data-testid="user-name">{user?.name}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                    <p className="font-medium text-slate-900" data-testid="user-email">{user?.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Status Card */}
            <Card className="shadow-sm border border-slate-200" data-testid="subscription-card">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">Subscription</CardTitle>
                  {getStatusBadge()}
                </div>
                <CardDescription>Your current plan and billing</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {subscriptionStatus?.is_admin ? (
                  <Alert className="bg-purple-50 border-purple-200">
                    <Crown className="h-5 w-5 text-purple-600" />
                    <AlertDescription className="text-purple-900">
                      <p className="font-semibold">Admin Account</p>
                      <p className="text-sm mt-1">You have unlimited access to all features.</p>
                    </AlertDescription>
                  </Alert>
                ) : subscriptionStatus?.status === "active" ? (
                  <Alert className="bg-emerald-50 border-emerald-200">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    <AlertDescription className="text-emerald-900">
                      <p className="font-semibold">{subscriptionStatus.plan === "yearly" ? "Yearly" : "Monthly"} Plan Active</p>
                      <p className="text-sm mt-1">
                        Valid until {new Date(subscriptionStatus.subscription_end).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'long', day: 'numeric' 
                        })}
                      </p>
                    </AlertDescription>
                  </Alert>
                ) : subscriptionStatus?.status === "trial" ? (
                  <>
                    <Alert className="bg-blue-50 border-blue-200">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      <AlertDescription className="text-blue-900">
                        <p className="font-semibold">Free Trial Active</p>
                        <p className="text-sm mt-1">{subscriptionStatus.trial_days_remaining} days remaining in your 1-week free trial</p>
                      </AlertDescription>
                    </Alert>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      {/* Monthly Plan */}
                      <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-lg">Monthly</CardTitle>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-slate-900">$4.99</span>
                            <span className="text-slate-500">/month</span>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <ul className="space-y-2 text-sm text-slate-600">
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Unlimited newsletters
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Deep web research
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Email scheduling
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Cancel anytime
                            </li>
                          </ul>
                          <Button 
                            data-testid="subscribe-monthly-btn"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleSubscribe('monthly')}
                            disabled={checkoutLoading !== null}
                          >
                            {checkoutLoading === 'monthly' ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                            ) : (
                              <><CreditCard className="mr-2 h-4 w-4" /> Subscribe Monthly</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Yearly Plan */}
                      <Card className="border-2 border-emerald-300 relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-emerald-600 hover:bg-emerald-600">2 Months Free</Badge>
                        </div>
                        <CardHeader className="pb-4">
                          <CardTitle className="text-lg">Yearly</CardTitle>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-slate-900">$49.90</span>
                            <span className="text-slate-500">/year</span>
                          </div>
                          <p className="text-xs text-emerald-600 font-medium">$4.16/month — save $9.98</p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <ul className="space-y-2 text-sm text-slate-600">
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Everything in Monthly
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              2 months free
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Priority support
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              Early access features
                            </li>
                          </ul>
                          <Button 
                            data-testid="subscribe-yearly-btn"
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleSubscribe('yearly')}
                            disabled={checkoutLoading !== null}
                          >
                            {checkoutLoading === 'yearly' ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                            ) : (
                              <><CreditCard className="mr-2 h-4 w-4" /> Subscribe Yearly</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                ) : (
                  <>
                    <Alert className="bg-red-50 border-red-200">
                      <Shield className="h-5 w-5 text-red-600" />
                      <AlertDescription className="text-red-900">
                        <p className="font-semibold">Subscription Expired</p>
                        <p className="text-sm mt-1">Subscribe to continue generating newsletters</p>
                      </AlertDescription>
                    </Alert>

                    {/* Same pricing cards for expired users */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-lg">Monthly</CardTitle>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-slate-900">$4.99</span>
                            <span className="text-slate-500">/month</span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Button 
                            data-testid="subscribe-monthly-btn"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleSubscribe('monthly')}
                            disabled={checkoutLoading !== null}
                          >
                            {checkoutLoading === 'monthly' ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                            ) : (
                              <><CreditCard className="mr-2 h-4 w-4" /> Subscribe Monthly</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-emerald-300 relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-emerald-600 hover:bg-emerald-600">2 Months Free</Badge>
                        </div>
                        <CardHeader className="pb-4">
                          <CardTitle className="text-lg">Yearly</CardTitle>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-slate-900">$49.90</span>
                            <span className="text-slate-500">/year</span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Button 
                            data-testid="subscribe-yearly-btn"
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleSubscribe('yearly')}
                            disabled={checkoutLoading !== null}
                          >
                            {checkoutLoading === 'yearly' ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                            ) : (
                              <><CreditCard className="mr-2 h-4 w-4" /> Subscribe Yearly</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* My Schedules Card */}
            <Card className="shadow-sm border border-slate-200" data-testid="my-schedules-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  My Schedules
                </CardTitle>
                <CardDescription>Manage your newsletter delivery schedules</CardDescription>
              </CardHeader>
              <CardContent>
                {schedulesLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : schedules.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No active schedules. Create one from the home page.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {schedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        data-testid={`schedule-item-${schedule.id}`}
                        className="border border-slate-200 rounded-lg p-4"
                      >
                        {editingId === schedule.id ? (
                          /* Edit Mode */
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-500">Role</Label>
                                <Input
                                  data-testid="edit-role-input"
                                  value={editForm.role}
                                  onChange={(e) => setEditForm(f => ({ ...f, role: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Topic</Label>
                                <Input
                                  data-testid="edit-prompt-input"
                                  value={editForm.prompt}
                                  onChange={(e) => setEditForm(f => ({ ...f, prompt: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <Label className="text-xs text-slate-500">Frequency</Label>
                                <Select
                                  data-testid="edit-frequency-select"
                                  value={editForm.frequency}
                                  onValueChange={(val) => setEditForm(f => ({ ...f, frequency: val }))}
                                >
                                  <SelectTrigger className="h-9 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="daily">Daily</SelectItem>
                                    <SelectItem value="weekly">Weekly</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {editForm.frequency === "weekly" && (
                                <div>
                                  <Label className="text-xs text-slate-500">Day</Label>
                                  <Select
                                    data-testid="edit-day-select"
                                    value={editForm.day_of_week}
                                    onValueChange={(val) => setEditForm(f => ({ ...f, day_of_week: val }))}
                                  >
                                    <SelectTrigger className="h-9 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => (
                                        <SelectItem key={d} value={d}>{d}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              <div>
                                <Label className="text-xs text-slate-500">Time (UTC)</Label>
                                <Input
                                  data-testid="edit-time-input"
                                  type="time"
                                  value={editForm.time}
                                  onChange={(e) => setEditForm(f => ({ ...f, time: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Colleague Emails (comma-separated)</Label>
                              <Input
                                data-testid="edit-colleagues-input"
                                value={editForm.colleague_emails}
                                onChange={(e) => setEditForm(f => ({ ...f, colleague_emails: e.target.value }))}
                                placeholder="email1@company.com, email2@company.com"
                                className="h-9 text-sm"
                              />
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button
                                data-testid="save-schedule-btn"
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => saveSchedule(schedule.id)}
                                disabled={savingId === schedule.id}
                              >
                                {savingId === schedule.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <Save className="h-4 w-4 mr-1" />
                                )}
                                Save
                              </Button>
                              <Button
                                data-testid="cancel-edit-btn"
                                size="sm"
                                variant="outline"
                                onClick={cancelEditing}
                              >
                                <X className="h-4 w-4 mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm truncate">{schedule.prompt}</p>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                <Badge variant="outline" className="text-xs">{schedule.role}</Badge>
                                <span className="capitalize">{schedule.frequency}</span>
                                {schedule.frequency === "weekly" && schedule.day_of_week && (
                                  <span>on {schedule.day_of_week}</span>
                                )}
                                <span>at {schedule.time} UTC</span>
                              </div>
                              {schedule.colleague_emails?.length > 0 && (
                                <p className="text-xs text-slate-400 truncate">
                                  Shared with: {schedule.colleague_emails.join(", ")}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1 ml-3 shrink-0">
                              <Button
                                data-testid={`edit-schedule-${schedule.id}`}
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-slate-500 hover:text-blue-600"
                                onClick={() => startEditing(schedule)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                data-testid={`delete-schedule-${schedule.id}`}
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-slate-500 hover:text-red-600"
                                onClick={() => deleteSchedule(schedule.id)}
                                disabled={deletingId === schedule.id}
                              >
                                {deletingId === schedule.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logout Card */}
            <Card className="shadow-sm border border-slate-200">
              <CardContent className="pt-6">
                <Button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full h-11 font-medium border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Feedback Section */}
        <FeedbackSection page="account" />
      </div>
    </div>
  );
};

export default AccountPage;
